import { Button } from '@abyss/web/ui/Button';
import { Checkbox } from '@abyss/web/ui/Checkbox';
import { CheckboxGroup } from '@abyss/web/ui/CheckboxGroup';
import { Flex } from '@abyss/web/ui/Flex';
import { Text } from '@abyss/web/ui/Text';
import difference from 'lodash/difference';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { PortalContext } from '../../context/PortalContext';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useLagoon } from '../../hooks/useLagoon';
import { capitalizeEachWord } from '../../utils/providerNames.utils';
import { getLagoonConfigValue } from '../../utils/providerSearch.utils';
import { adobeLinkTrackEvent } from '../AdobeTagging/adobeLinkTrackEvent';
import { EAP_OPTION_LABEL, FilterCollectionModel } from '../Constants';
import { ConstantsLagoon } from '../ConstantsLagoon';
import { BoardCertifiedInfoIcon } from './BoardCertifiedInfoIcon';
import { CheckBoxPopover } from './CheckBoxInfoPopover';
import { CulturalCompetencyTrainingIcon } from './CulturalCompetencyTrainingIcon';
import { FilterSearchInput } from './FilterSearchInput';
import { NetworkAffiliationsInfoIcon } from './NetworkAffiliationsInfoIcon';
import { PlanTypeDisabledInfoIcon } from './PlanTypeDisabledInfoIcon';

const buttonStyles: any = {
  'abyss-button-root': {
    height: '42px',
    marginTop: '20px',
    border: 'none',
    borderRadius: '24px',
  },
};

// Module-level Set to track if user has interacted with Network Affiliations filter
// This persists across drawer open/close cycles (component mount/unmount)
// Prevents URL params from re-selecting options after user has manually changed selection
const networkAffiliationsUserInteracted = new Set<string>();

type Radio = {
  label: String;
  value: string | number | boolean;
  count?: number;
  isDisabled?: boolean;
};

type Option = {
  label: String;
  value: string | number | boolean;
  count?: number;
  isDisabled?: boolean;
  dnt_label: String;
};

interface Props {
  groupLabel?: string;
  options: Array<Radio> | Array<Option>;
  defaultValue?:
    | string[]
    | number[]
    | boolean[]
    | (string | number | boolean)[];
  disableSubmitBtn?: boolean;
  isDisabled?: boolean;
  model: string;
  onApply?: (currentItem: string[]) => void;
  onOptionChange?: (any) => void;
  submitBtnName?: string;
  labelFontSize?: string;
  locationForAnalytics?: string;
}

export const FilterCheckBoxGroup = ({
  options,
  defaultValue,
  groupLabel,
  model,
  onOptionChange = () => {},
  onApply = () => {},
  submitBtnName,
  disableSubmitBtn = false,
  isDisabled = false,
  labelFontSize = '16px',
  locationForAnalytics,
}: Props) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  // Get networkAffiliationId from URL query params
  const networkAffiliationIdFromParams = searchParams.get(
    'networkAffiliationId'
  );

  const [currentItem, setCurrentItem] = useState(
    defaultValue?.map(String) || []
  );

  // Check if user has already interacted with this specific network affiliation filter
  // Uses module-level Set to persist across drawer open/close cycles
  const hasUserInteracted = networkAffiliationIdFromParams
    ? networkAffiliationsUserInteracted.has(networkAffiliationIdFromParams)
    : false;

  const [searchTerm, setSearchTerm] = useState('');
  // Snapshot of selected items at the time the search term changes.
  // These are shown at the top during search; items selected during search stay in place.
  const preSelectedSnapshot = useRef<string[]>(defaultValue?.map(String) || []);
  // Capture the initial default value at mount time for non-search reordering
  const initialDefaultValue = useRef<string[]>(defaultValue?.map(String) || []);
  const [enableEapFilterUsingBHProgramFF, enableFilterSearch] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_EAP_FILTER_USING_BH_PROGRAM,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_FILTER_SEARCH,
  ]);
  const [enableNetworkAffiliationFlow] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_NETWORK_AFFILIATION_FLOW,
  ]);
  const { portalData } = useContext(PortalContext);
  const portalLockEAPfilter = portalData?.portalConfig?.lockEapFilter;

  // Lagoon config for filter search
  const allConfigs = useLagoon('config');
  const filterSearchThreshold =
    getLagoonConfigValue(
      allConfigs,
      ConstantsLagoon.CONFIG.FILTER_SEARCH_THRESHOLD
    ) || 10;

  const uiMessagingData = useLagoon('ui-messaging')();
  const noResultsMessage = find(uiMessagingData, {
    key: ConstantsLagoon.CONTENT_FILTERS.FILTER_SEARCH_NO_RESULTS,
  });

  const showSearchInput =
    enableFilterSearch && options.length > filterSearchThreshold;

  // Update the pre-selected snapshot whenever search term changes
  useEffect(() => {
    if (showSearchInput) {
      preSelectedSnapshot.current = [...currentItem];
    }
  }, [searchTerm]);

  // Filter options based on search term, with pre-selected at top
  const filteredOptions = useMemo(() => {
    const withStringValues = options.map((option) => ({
      ...option,
      value: option.value.toString(),
    }));

    // Separate pre-selected items and the rest
    const preSelected: typeof withStringValues = [];
    const rest: typeof withStringValues = [];

    withStringValues.forEach((option) => {
      const isPreSelected = showSearchInput
        ? preSelectedSnapshot.current.includes(option.value)
        : initialDefaultValue.current.includes(option.value);

      if (isPreSelected) {
        preSelected.push(option);
      } else {
        rest.push(option);
      }
    });

    // Sort pre-selected alphabetically
    preSelected.sort((a, b) =>
      a.label.toString().localeCompare(b.label.toString())
    );

    // If searching, filter the non-pre-selected items by search term
    const filteredRest =
      showSearchInput && searchTerm.trim()
        ? rest.filter((option) =>
            option.label
              .toString()
              .toLowerCase()
              .includes(searchTerm.toLowerCase())
          )
        : rest;

    return [...preSelected, ...filteredRest];
  }, [options, searchTerm, showSearchInput]);

  // Helper function to check if an option should be selected for Network Affiliations
  // Network Affiliations options have comma-separated values, so we need to check if
  // any of the currentItem values are contained within the option's comma-separated value
  const isOptionSelected = (optionValue: string, selectedItems: string[]) => {
    if (model === FilterCollectionModel.NETWORK_AFFILIATIONS) {
      // First check for exact value match (when user selected this option directly)
      if (selectedItems.includes(optionValue)) {
        return true;
      }
      // Check if the option value contains any of the selected IDs
      const optionIds = optionValue.split(',').map((id) => id.trim());
      // Only use URL params if user hasn't interacted yet
      if (
        !hasUserInteracted &&
        networkAffiliationIdFromParams &&
        optionIds.includes(networkAffiliationIdFromParams)
      ) {
        return true;
      }
      // Fallback: check if any selected ID matches option IDs (for URL param cases)
      return selectedItems.some((selectedId) => optionIds.includes(selectedId));
    }
    return selectedItems.includes(optionValue);
  };

  // For Network Affiliations, find the full option value that contains the selected ID
  const getEffectiveCurrentItems = () => {
    if (model === FilterCollectionModel.NETWORK_AFFILIATIONS) {
      const effectiveItems: string[] = [];

      // Only use URL params for initial selection (before user interaction)
      if (!hasUserInteracted && networkAffiliationIdFromParams) {
        options.forEach((option) => {
          const optionValue = option.value.toString();
          const optionIds = optionValue.split(',').map((id) => id.trim());
          if (optionIds.includes(networkAffiliationIdFromParams)) {
            effectiveItems.push(optionValue);
          }
        });
      }

      // Also check against currentItem values
      if (currentItem.length > 0) {
        options.forEach((option) => {
          const optionValue = option.value.toString();
          // First check for exact value match (when user selected this option directly)
          if (currentItem.includes(optionValue)) {
            if (!effectiveItems.includes(optionValue)) {
              effectiveItems.push(optionValue);
            }
          } else {
            // Fallback: check if any currentItem ID matches option IDs (for URL param cases)
            const optionIds = optionValue.split(',').map((id) => id.trim());
            if (
              currentItem.some((selectedId) => optionIds.includes(selectedId))
            ) {
              if (!effectiveItems.includes(optionValue)) {
                effectiveItems.push(optionValue);
              }
            }
          }
        });
      }

      return effectiveItems.length > 0 ? effectiveItems : currentItem;
    }
    return currentItem;
  };

  const effectiveCurrentItem = getEffectiveCurrentItems();

  useEffect(() => {
    setCurrentItem(defaultValue?.map(String) || []);
  }, [JSON.stringify(defaultValue)]);

  const onSubmit = () => {
    onApply(currentItem);
  };

  const getLabelName = (filter: string) => {
    const label = options
      .find((option) => option.value.toString() === filter)
      ?.label.concat(`_${filter}`);
    return label;
  };

  function onChangeCheckboxGroupOption(e) {
    // Prevent unchecking the last option for planType filter
    if (
      model === FilterCollectionModel.PLAN_TYPE &&
      currentItem.length === 1 &&
      e.length === 0
    ) {
      // Do not allow clearing all selections for planType
      return;
    }

    // Mark that user has interacted with Network Affiliations filter
    // This prevents URL params from re-selecting options after user unselects
    // Uses module-level Set to persist across drawer open/close cycles
    if (
      model === FilterCollectionModel.NETWORK_AFFILIATIONS &&
      networkAffiliationIdFromParams
    ) {
      networkAffiliationsUserInteracted.add(networkAffiliationIdFromParams);
    }

    let linkName;

    if (!isEqual(currentItem, e)) {
      if (isEmpty(currentItem) && e?.length === 1) {
        linkName = `${getLabelName(e[0])}:check`;
      } else {
        let updatedOption;
        if (e?.length > currentItem?.length) {
          updatedOption = difference(e, currentItem);
          linkName = `${getLabelName(updatedOption[0])}:check`;
        }
        if (e?.length < currentItem?.length) {
          updatedOption = difference(currentItem, e);
          linkName = `${getLabelName(updatedOption[0])}:uncheck`;
        }
      }
      adobeLinkTrackEvent({
        name: linkName,
        location: locationForAnalytics,
        type: 'checkbox',
      });
    }

    setCurrentItem(e);
    onOptionChange({
      [model]: options.filter((option) => e.includes(option.value.toString())),
    });
  }

  function listOfLabels(label: String, model: string) {
    if (
      model === t('CONTENT_FILTERS.HOSPITAL_AFFILIATIONS').replace(/\s/g, '')
    ) {
      return capitalizeEachWord(label);
    } else {
      return label;
    }
  }

  const getNoResultsMessage = () => {
    if (!noResultsMessage?.message) {
      return t('CONTENT_FILTERS.FILTER_SEARCH_NO_RESULTS_DEFAULT', {
        searchTerm,
      });
    }
    // Replace placeholder in Lagoon message with search term
    return noResultsMessage.message.replace('{searchTerm}', searchTerm);
  };

  return (
    <React.Fragment>
      <Flex
        css={{
          'abyss-flex-root': {
            position: 'relative',
            overflow: 'visible',
          },
        }}
        direction="column"
      >
        {showSearchInput && (
          <FilterSearchInput onChange={setSearchTerm} value={searchTerm} />
        )}
      </Flex>
      <Flex
        css={{
          'abyss-flex-root': {
            maxHeight:
              'calc(var(--radix-popper-available-height, 70vh) - 220px)',
            overflow: 'auto',
            overflowX: 'hidden',
            paddingLeft: '4px',
            paddingRight: '4px',
            paddingBottom: '4px',
          },
        }}
        direction="column"
      >
        {filteredOptions.length === 0 && searchTerm ? (
          <Flex
            css={{
              padding: '20px',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Text
              color="$gray7"
              data-auto-testid="filter-search-no-results"
              data-testid="filter-search-no-results"
            >
              {getNoResultsMessage()}
            </Text>
          </Flex>
        ) : (
          <CheckboxGroup
            css={{
              'abyss-checkbox-group-label': {
                margin: '0',
              },
              'abyss-checkbox-group-root': {
                border: 'none',
              },
            }}
            data-auto-testid={`${model}-group`}
            data-testid={`${model}-group`}
            label={groupLabel}
            onChange={(e) => {
              onChangeCheckboxGroupOption(e);
            }}
            validators={{ required: false }}
            value={effectiveCurrentItem}
          >
            {filteredOptions.map(
              ({
                value,
                label,
                count,
                isDisabled: optionDisabled,
                dnt_label,
              }) => {
                const shouldBeDisabled =
                  count === 0 ||
                  optionDisabled ||
                  (portalLockEAPfilter && dnt_label === EAP_OPTION_LABEL);
                const isCheckboxDisabled = shouldBeDisabled || isDisabled;

                const displayLabel =
                  count || count === 0
                    ? `${label} (${count})`
                    : listOfLabels(label, model);

                return (
                  <Checkbox
                    css={{
                      'abyss-checkbox-root': {
                        padding: '12px 0px 12px $xs',
                        margin: '0 !important',
                        alignItems: 'center !important',
                        display:
                          model === FilterCollectionModel.AREA_OF_EXPERTISE &&
                          dnt_label === EAP_OPTION_LABEL &&
                          enableEapFilterUsingBHProgramFF
                            ? 'none'
                            : 'initial',
                      },
                      'abyss-checkbox-label': {
                        fontSize: labelFontSize,
                        color: isCheckboxDisabled
                          ? '$gray5'
                          : !isCheckboxDisabled &&
                            (isOptionSelected(value.toString(), currentItem)
                              ? '$gray8'
                              : '$gray7'),
                        fontWeight:
                          !isCheckboxDisabled &&
                          (isOptionSelected(value.toString(), currentItem)
                            ? '$bold'
                            : '$medium'),
                      },
                    }}
                    data-testid="checkbox-selection"
                    isDisabled={isCheckboxDisabled}
                    key={label.concat(`_${value}`)}
                    label={displayLabel}
                    value={value.toString()}
                  />
                );
              }
            )}
          </CheckboxGroup>
        )}
        {model === FilterCollectionModel.PLAN_TYPE &&
          options.some((o) => o.isDisabled) && <PlanTypeDisabledInfoIcon />}
        {model ===
        FilterCollectionModel.BOARD_CERTIFIED_SPECIALITY_INDICATOR ? (
          <BoardCertifiedInfoIcon />
        ) : null}
      </Flex>
      {model === FilterCollectionModel.CULTURAL_COMPETENCE ? (
        <CulturalCompetencyTrainingIcon />
      ) : null}
      {enableNetworkAffiliationFlow &&
      model === FilterCollectionModel.NETWORK_AFFILIATIONS ? (
        <NetworkAffiliationsInfoIcon />
      ) : null}
      {(model === FilterCollectionModel.PLATINUM_PROVIDER_INDICATOR ||
        model === FilterCollectionModel.PRIORITY_SCHEDULING) && (
        <CheckBoxPopover filterModel={model} />
      )}
      {submitBtnName ? (
        <Button
          css={buttonStyles}
          data-auto-testid="apply-button"
          data-testid="apply-button"
          isDisabled={disableSubmitBtn}
          onClick={onSubmit}
        >
          {submitBtnName}
        </Button>
      ) : null}
    </React.Fragment>
  );
};
